import Image from "components/atoms/Image";
import { colorBlack, KIKUSHIRU_BASE_URL } from "lib/constants";
import { unixTimestampToDate } from "lib/utils/dateHelper";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import React, { VFC, memo } from "react";
import styled from "styled-components";
import { getMypageUrl } from "~/lib/utils/urlHelper";
import { pagesPath } from "faqSrc/lib/$path";
import LocaleFlags from "./LocaleFlags";

interface Props {
  asPath?: string;
  languageAlts?: string[];
}

const FooterPresenter: VFC<Props> = ({ asPath, languageAlts }) => {
  const { t } = useTranslation();
  const currentYear = unixTimestampToDate(Date.now() / 1000, "Y");
  return (
    <Container id="footer">
      <div className="content-wrap">
        <LocaleFlagsContainer>
          <LocaleFlags asPath={asPath} languageAlts={languageAlts} />
        </LocaleFlagsContainer>
        <div>
          <Link href={pagesPath.$url()} passHref>
            <ItemLink>
              {t("common:footer.home", {}, { fallback: "トップページ" })}
            </ItemLink>
          </Link>
          <Link href={pagesPath.qa.$url()} passHref>
            <ItemLink>
              {t("common:footer.topics", {}, { fallback: "Q&Aトピック一覧" })}
            </ItemLink>
          </Link>
          <Link href={pagesPath.creators.$url()} passHref>
            <ItemLink>
              {t(
                "common:footer.creators",
                {},
                { fallback: "クリエイター紹介" }
              )}
            </ItemLink>
          </Link>
          <Link href={pagesPath.terms.$url()} passHref>
            <ItemLink>
              {t("common:footer.terms", {}, { fallback: "利用規約" })}
            </ItemLink>
          </Link>
          <Link href={pagesPath.privacy_policy.$url()} passHref>
            <ItemLink>
              {t(
                "common:footer.privacy-policy",
                {},
                { fallback: "プライバシー規定" }
              )}
            </ItemLink>
          </Link>
          <Link href={`${getMypageUrl()}/login`} passHref>
            <ItemLink>
              {t(
                "common:footer.login",
                {},
                { fallback: "クリエイターログイン" }
              )}
            </ItemLink>
          </Link>
        </div>
        <Copyright>
          <PoweredText>
            <ServiceSiteLink
              href={KIKUSHIRU_BASE_URL}
              target="_blank"
              rel="noopener"
            >
              © {currentYear}{" "}
              {t("common:kikushiru-site-name", {}, { fallback: "キクシル" })}
            </ServiceSiteLink>
          </PoweredText>
        </Copyright>
        <QuestionMarkLink
          href={KIKUSHIRU_BASE_URL}
          target="_blank"
          rel="noopener"
          aria-label="キクシル"
        >
          <Image
            alt="キクシル ロゴ"
            src="/img/question-mark.svg"
            width="40"
            height="40"
            disableHover={true}
          />
        </QuestionMarkLink>
      </div>
    </Container>
  );
};

const Container = styled.div`
  background-color: var(--cont-bg-clr);
  border-top: 1px solid ${colorBlack[50]};
  padding: 40px 16px 100px 16px;
  text-align: center;
  position: relative;
  z-index: 10;
`;

const ItemLink = styled.a`
  font-weight: 700;
  font-size: 16px;
  line-height: 30px;
  text-decoration-line: underline;
  color: var(--cap-clr);
  margin: 0 12px 16px 12px;
  display: inline-block;

  &:hover,
  &:active {
    opacity: 0.8;
    text-decoration: none;
  }
`;

const Copyright = styled.div`
  font-size: 16px;
  line-height: 30px;
  color: var(--cap-clr);
`;

const PoweredText = styled.span`
  display: inline-block;
  margin-bottom: 16px;
`;

const ServiceSiteLink = styled.a`
  color: var(--cap-clr);
  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

const QuestionMarkLink = styled.a`
  display: block;
`;

const LocaleFlagsContainer = styled.div`
  display: grid;
  justify-content: center;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, 32px);
  margin-bottom: 24px;
`;

export default memo(FooterPresenter);
